import {
  Component,
  Expressions,
  Plugins,
  Type,
  Types,
} from '@packages/systems/wfdl';
import {ELiteral, ERecord, Expression} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  parseVariantStateKey,
  VariantStateKey,
} from '@packages/systems/style-legacy/selectors';
import {BASE_VARIANT_DEFAULTS} from '@packages/systems/component-style-variants';

const styleVariantType = qualify(
  Plugins.name('Basic'),
  typeName('StyleVariant')
);

/**
 * Given an element's dataType and data, prunes the data expression to only include style-variant fields
 * and returns the pruned expression.
 * The currently focused component has its variant set based on the variantState.
 */
export function pruneElementDataToStyleVariants(
  dataType: Type,
  dataExpr: Expression,
  isEvaluatingFocusedComponent: boolean,
  variantState: VariantStateKey | null,
  component: Component.ComponentType
): Expression {
  const fields =
    Types.getRecordFields(dataType) ?? ({} as Record<string, Type>);
  const record = Expressions.getRecord(dataExpr);
  if (!record) return dataExpr;

  const styleVariantFields: Record<string, Expression> = {};
  for (const key of Object.keys(record)) {
    const field = fields[key];
    if (field && Types.isStyleVariantType(field)) {
      if (record[key]) styleVariantFields[key] = record[key];
    }
  }
  // If we are evaluating the focused component, we want to set the variant based on the variantState
  // If variantState is not set, we want to use the base variant as a null variant state represents the base variant being set.
  if (isEvaluatingFocusedComponent) {
    const variants = Component.getVariants(component);
    if (!variants) return ERecord(styleVariantFields);

    // We currently support only one variant field
    const variantFieldId = Object.keys(variants)[0];
    if (!variantFieldId) return ERecord(styleVariantFields);
    const [_, variantOptionId] =
      variantState != null
        ? parseVariantStateKey(variantState)
        : [null, BASE_VARIANT_DEFAULTS.id];
    styleVariantFields[variantFieldId] = ELiteral({
      name: styleVariantType,
      value: variantOptionId,
    });
  }
  return ERecord(styleVariantFields);
}
